import { FC, PropsWithChildren, createContext, useContext } from 'react';
import FleetModal, { useFleetModal } from '@/libs/antd/components/Modal';
import CustomerDetailModal, {
	useCustomerDetailModal,
} from './CustomerDetailModal';
import DocumentModal, { DocumentItem, useDocumentModal } from './DocumentModal';
import PreviewModal, { usePreviewModal } from './PreviewModal';
import CustomerGroupDetailModal, {
	useCustomerGroupDetailModal,
} from './CustomerGroupDetailModal';
import ProspectDetailModal, {
	useProspectDetailModal,
} from './ProspectDetailModal';

import ManagementModal, {
	ManagementModalState,
	useManagementModal,
} from './ManagementModal';
import ShareholderModal, {
	ShareholderModalState,
	useShareholderModal,
} from './ShareholderModal';
import { FleetFile } from '@/libs/fleet-api/fetcher';
import MapModal, { MapModalState, useMapModal } from './MapModal';
import CustomerCheckingModal, {
	useCustomerCheckingModal,
} from './CustomerCheckingModal';
import PraSurveyAnalysisModal, {
	usePraSurveyAnalysisModal,
} from './PraSurveyAnalysisModal';
import PraSurveyDataModal, {
	usePraSurveyDataModal,
} from './PraSurveyDataModal';
import PipelineModal, { usePipelineModal } from './PipelineModal';
import ApprovalHistoryModal, {
	useApprovalHistoryModal,
} from './ApprovalHistoryModal';
import NegativeHistoryModal, {
	useNegativeHistoryModal,
} from './NegativeHistoryModal';
import RejectHistoryModal, {
	useRejectHistoryModal,
} from './RejectHistoryModal';
import FinancialReportModal, {
	useFinancialReportModal,
} from './FinancialReportModal';
import BankStatementModal, {
	useBankStatementModal,
} from './BankStatementModal';
import EmploymentContractModal, {
	useEmploymentContractModal,
} from './EmploymentContractModal';
import { customerNegativeHistory } from '@/libs/fleet-api/services/prospect';
import {
	FilePayload,
	ProspectBankStatement,
	ProspectEmploymentContract,
	ProspectFinancialReport,
} from '@/libs/fleet-api/services/types';
import GenerateLKPDFModal, {
	GenerateLKPDFModalState,
	useGenerateLKPDFModal,
} from './GenerateLKPDFModal';
import NAPDownloadModal, {
	NAPDownloadModalState,
	useNAPDownloadModal,
} from './NAPDownloadModal';
import GenerateCallReportPDFModal, {
	GenerateCallReportPDFModalState,
	useGenerateCallReportPDFModal,
} from './GenerateCallReportPDFModal';
import PreviewDocumentModal, {
	PreviewDocumentModalParam,
	usePreviewDocumentModal,
} from './PreviewDocumentModal';
import PurchaseOrderDetailModal, {
	usePurchaseOrderDetailModal,
} from './PurchaseOrderDetailModal';

type ModalContext = {
	modal: ReturnType<typeof useFleetModal>[0];
	openCustomerDetail: (customerId: string) => void;
	openCustomerGroupDetail: (customerGroupId: number | string) => void;
	openDocumentModal: (documents: DocumentItem[]) => void;
	openProspectDetail: (prospectId: string) => void;
	openApprovalHistory: (prospectId: string) => void;
	openManagementModal: (payload: NonNullable<ManagementModalState>) => void;
	openShareholderModal: (payload: NonNullable<ShareholderModalState>) => void;
	openPreviewModal: (file: FleetFile | FilePayload) => void;
	openPreviewDocumentModal: (param: PreviewDocumentModalParam) => Promise<void>;
	openMapModal: (mapData: MapModalState) => void;
	openCustomerCheckingModal: (prospectId: string) => void;
	openPraSurveyAnalysisModal: (prospectId: string) => void;
	openPraSurveyDataModal: (prospectId: string) => void;
	openPipelineModal: (pipelineHotProspectId: number) => void;
	openNegativeHistoryModal: (
		customerNegativeHistory: customerNegativeHistory,
	) => void;
	openRejectHistoryModal: (rejectionId: number) => void;
	openFinancialReportModal: (financialReport: ProspectFinancialReport) => void;
	openBankStatementModal: (bankStatement: ProspectBankStatement) => void;
	openEmploymentContractModal: (
		employmentContract: ProspectEmploymentContract,
	) => void;
	openLKPDFModal: (state: GenerateLKPDFModalState) => void;
	openNAPDownloadModal: (state: NAPDownloadModalState) => void;
	openCallReportPDFModal: (state: GenerateCallReportPDFModalState) => void;
	openPurchaseOrderDetail: (param: {
		mppId: number;
		purchaseOrderId: number;
		mklNo?: string | null;
		spfNo?: string | null;
	}) => void;
};

const modalContext = createContext<ModalContext | null>(null);
const { Provider } = modalContext;

export const useModals = () => {
	return useContext(modalContext)!;
};

const ModalProvider: FC<PropsWithChildren> = ({ children }) => {
	const [modal, modalProps] = useFleetModal();

	const [customerModalDetail, setCustomerModalDetail] =
		useCustomerDetailModal();

	const openCustomerDetail: ModalContext['openCustomerDetail'] = (
		customerId,
	) => {
		setCustomerModalDetail({ customerId });
	};

	const [customerGroupModalDetail, setCustomerGroupModalDetail] =
		useCustomerGroupDetailModal();

	const [managementModal, setManagementModal] = useManagementModal();
	const [shareholderModal, setShareholderModal] = useShareholderModal();

	const [prospectModalDetail, setProspectModalDetail] =
		useProspectDetailModal();

	const [approvalHistoryModal, setApprovalHistoryModal] =
		useApprovalHistoryModal();

	const openProspectDetail: ModalContext['openProspectDetail'] = (
		prospectId,
	) => {
		setProspectModalDetail({ prospectId });
	};

	const openApprovalHistory: ModalContext['openApprovalHistory'] = (
		prospectId,
	) => {
		setApprovalHistoryModal({ prospectId });
	};

	const [negativeHistoryModal, setNegativeHistoryModal] =
		useNegativeHistoryModal();

	const openNegativeHistoryModal: ModalContext['openNegativeHistoryModal'] = (
		customerNegativeHistory,
	) => {
		setNegativeHistoryModal({ customerNegativeHistory });
	};

	const [rejectHistoryModal, setRejectHistoryModal] = useRejectHistoryModal();

	const openRejectHistoryModal: ModalContext['openRejectHistoryModal'] = (
		rejectionId,
	) => {
		setRejectHistoryModal({ rejectionId });
	};

	const [financialReportModal, setFinancialReportModal] =
		useFinancialReportModal();

	const openFinancialReportModal: ModalContext['openFinancialReportModal'] = (
		financialReport,
	) => {
		setFinancialReportModal({ financialReport });
	};

	const [bankStatementModal, setBankStatementModal] = useBankStatementModal();

	const openBankStatementModal: ModalContext['openBankStatementModal'] = (
		bankStatement,
	) => {
		setBankStatementModal({ bankStatement });
	};

	const [employmentContractModal, setEmploymentContractModal] =
		useEmploymentContractModal();

	const openEmploymentContractModal: ModalContext['openEmploymentContractModal'] =
		(employmentContract) => {
			setEmploymentContractModal({ employmentContract });
		};

	const openCustomerGroupDetail = (customerGroupId: number | string) => {
		setCustomerGroupModalDetail({ customerGroupId });
	};

	const openManagementModal = (payload: NonNullable<ManagementModalState>) => {
		setManagementModal(payload);
	};
	const openShareholderModal = (
		payload: NonNullable<ShareholderModalState>,
	) => {
		setShareholderModal(payload);
	};

	const [documentModal, setDocumentModal] = useDocumentModal();
	const openDocumentModal: ModalContext['openDocumentModal'] = (documents) => {
		setDocumentModal(documents);
	};

	const [previewModal, setPreviewModal] = usePreviewModal();
	const openPreviewModal: ModalContext['openPreviewModal'] = (file) => {
		setPreviewModal(file);
	};

	const previewDocumentModal = usePreviewDocumentModal();
	const openPreviewDocumentModal = (param: PreviewDocumentModalParam) => {
		return previewDocumentModal.open(param);
	};

	const [mapModal, setMapModal] = useMapModal();
	const openMapModal: ModalContext['openMapModal'] = (mapData) => {
		setMapModal(mapData);
	};

	const [customerCheckingModal, setCustomerCheckingModal] =
		useCustomerCheckingModal();
	const openCustomerCheckingModal: ModalContext['openCustomerCheckingModal'] = (
		prospectId: string,
	) => {
		setCustomerCheckingModal({ prospectId });
	};

	const [praSurveyAnalysisModal, setPraSurveyAnalysisModal] =
		usePraSurveyAnalysisModal();
	const openPraSurveyAnalysisModal: ModalContext['openPraSurveyAnalysisModal'] =
		(prospectId: string) => {
			setPraSurveyAnalysisModal({ prospectId });
		};

	const [praSurveyDataModal, setPraSurveyDataModal] = usePraSurveyDataModal();
	const openPraSurveyDataModal: ModalContext['openPraSurveyDataModal'] = (
		prospectId: string,
	) => {
		setPraSurveyDataModal({ prospectId });
	};

	const [pipelineModal, setPipelineModal] = usePipelineModal();
	const openPipelineModal: ModalContext['openPipelineModal'] = (
		pipelineHotProspectId: number,
	) => {
		setPipelineModal({ pipelineId: pipelineHotProspectId });
	};

	const [generateLKPDFmodal, setGenerateLKPDFmodal] = useGenerateLKPDFModal();
	const openLKPDFModal = (config: GenerateLKPDFModalState) => {
		setGenerateLKPDFmodal(config);
	};

	const [napDownloadModal, setNapDownloadModal] = useNAPDownloadModal();
	const openNAPDownloadModal = (config: NAPDownloadModalState) => {
		setNapDownloadModal(config);
	};

	const [generateCallReportPDFModal, setGenerateCallReportPDFModal] =
		useGenerateCallReportPDFModal();
	const openCallReportPDFModal = (config: GenerateCallReportPDFModalState) => {
		setGenerateCallReportPDFModal(config);
	};

	const [purchaseOrderDetailModal, setPurchaseOrderDetailModal] =
		usePurchaseOrderDetailModal();
	const openPurchaseOrderDetail: ModalContext['openPurchaseOrderDetail'] = (
		param,
	) => {
		setPurchaseOrderDetailModal(param);
	};

	return (
		<Provider
			value={{
				modal,
				openCustomerDetail,
				openCustomerGroupDetail,
				openDocumentModal,
				openProspectDetail,
				openApprovalHistory,
				openManagementModal,
				openShareholderModal,
				openPreviewModal,
				openPreviewDocumentModal,
				openMapModal,
				openCustomerCheckingModal,
				openPraSurveyAnalysisModal,
				openPraSurveyDataModal,
				openPipelineModal,
				openNegativeHistoryModal,
				openFinancialReportModal,
				openBankStatementModal,
				openEmploymentContractModal,
				openRejectHistoryModal,
				openLKPDFModal,
				openNAPDownloadModal,
				openCallReportPDFModal,
				openPurchaseOrderDetail,
			}}
		>
			{children}

			<FleetModal {...modalProps} />

			<ProspectDetailModal
				modal={prospectModalDetail}
				onClose={() => {
					setProspectModalDetail(null);
				}}
			/>

			<ApprovalHistoryModal
				modal={approvalHistoryModal}
				onClose={() => {
					setApprovalHistoryModal(null);
				}}
			/>

			<CustomerDetailModal
				modal={customerModalDetail}
				onClose={() => {
					setCustomerModalDetail(null);
				}}
			/>

			<CustomerGroupDetailModal
				modal={customerGroupModalDetail}
				onClose={() => {
					setCustomerGroupModalDetail(null);
				}}
			/>

			<ManagementModal
				modal={managementModal}
				onClose={() => {
					setManagementModal(null);
				}}
			/>

			<ShareholderModal
				modal={shareholderModal}
				onClose={() => {
					setShareholderModal(null);
				}}
			/>

			<DocumentModal
				modal={documentModal}
				onClose={() => {
					setDocumentModal(null);
				}}
			/>

			<PreviewModal
				file={previewModal}
				onClose={() => {
					setPreviewModal(null);
				}}
			/>

			<PreviewDocumentModal {...previewDocumentModal} />

			<MapModal
				mapData={mapModal}
				onClose={() => {
					setMapModal(null);
				}}
			/>

			<CustomerCheckingModal
				modal={customerCheckingModal}
				onClose={() => {
					setCustomerCheckingModal(null);
				}}
			/>

			<PraSurveyAnalysisModal
				modal={praSurveyAnalysisModal}
				onClose={() => {
					setPraSurveyAnalysisModal(null);
				}}
			/>

			<PraSurveyDataModal
				modal={praSurveyDataModal}
				onClose={() => {
					setPraSurveyDataModal(null);
				}}
			/>

			<PipelineModal
				modal={pipelineModal}
				onClose={() => {
					setPipelineModal(null);
				}}
			/>

			<NegativeHistoryModal
				modal={negativeHistoryModal}
				onClose={() => {
					setNegativeHistoryModal(null);
				}}
			/>
			<RejectHistoryModal
				modal={rejectHistoryModal}
				onClose={() => {
					setRejectHistoryModal(null);
				}}
			/>

			<FinancialReportModal
				modal={financialReportModal}
				onClose={() => {
					setFinancialReportModal(null);
				}}
			/>

			<BankStatementModal
				modal={bankStatementModal}
				onClose={() => {
					setBankStatementModal(null);
				}}
			/>

			<EmploymentContractModal
				modal={employmentContractModal}
				onClose={() => {
					setEmploymentContractModal(null);
				}}
			/>

			<GenerateLKPDFModal
				modal={generateLKPDFmodal}
				onClose={() => setGenerateLKPDFmodal(null)}
			/>

			<GenerateCallReportPDFModal
				modal={generateCallReportPDFModal}
				onClose={() => setGenerateCallReportPDFModal(null)}
			/>

			<NAPDownloadModal
				modal={napDownloadModal}
				onClose={() => setNapDownloadModal(null)}
			/>

			<PurchaseOrderDetailModal
				modal={purchaseOrderDetailModal}
				onClose={() => setPurchaseOrderDetailModal(null)}
			/>
		</Provider>
	);
};

export default ModalProvider;
